/* topiary concept A (5 Oct 2026; 2c, 3.3): desktop product page behaviour, 1025 px and wider.
   Plan: Docs/pdp-concept-a-build-plan-2026-10-05.md (draft 3, Codex GO).

   - Delivery card: reads the date and cut-off the Essential Estimated Delivery app already shows, so the app stays the
     single source for holidays and cut-off. Tiles and the button total: read the Essential Upsell volume tiers.
   - Fail open: if anything can't be read, our card / tiles / total are removed and the app's own block shows again.
     Everything is re-checked after each variant change and whenever the app blocks change.
   - Our markup is hidden below 1025 px by topiary-pdp-desktop.css, and this script does nothing there. */
(() => {
  'use strict';
  const DESKTOP = window.matchMedia('(min-width: 1025px)');
  // Release gate (plan section 5, test B): the discounted tile prices and the button total ship only if the checkout
  // reconciliation passes. If it fails, set this to false: the tiles and total are then never drawn.
  const SHOW_DISCOUNTED = true;
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const root = () => document.querySelector('product-info.main-product-view');
  const gbp = (pence) => '£' + (pence / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const pad = (n) => String(n).padStart(2, '0');

  /* ------------------------------------------------------------------ data readers (null = can't read: fail open) */
  function readDelivery() {
    const r = root(); if (!r) return null;
    const block = r.querySelector('[data-block-key*="essential_estimated"]'); if (!block) return null;
    const dateEl = block.querySelector('variable-display[data-key="{delivery_to}"]');
    const timer = block.querySelector('timer-display[cutoff-date]');
    if (!dateEl || !timer) return null;
    const m = (dateEl.textContent || '').trim().match(/^([A-Z][a-z]{2})\s+(\d{1,2})$/);
    const cutoff = new Date(timer.getAttribute('cutoff-date'));
    if (!m || MONTHS.indexOf(m[1]) < 0 || isNaN(cutoff.getTime())) return null;
    const now = new Date();
    let year = now.getFullYear();
    const month = MONTHS.indexOf(m[1]);
    if (month < now.getMonth() - 6) year += 1;                        // "Jan 04" read in December
    const date = new Date(year, month, Number(m[2]));
    if (date.getDate() !== Number(m[2]) || date < new Date(now.getFullYear(), now.getMonth(), now.getDate())) return null;
    if (cutoff.getTime() < now.getTime() - 60000 || cutoff.getTime() > now.getTime() + 8 * 864e5) return null;
    return { block, date, cutoff, text: dateEl.textContent.trim() };
  }

  function readTiers() {
    try {
      const funnels = (window.essentialUpsellConfigs && window.essentialUpsellConfigs.funnels) || [];
      const f = funnels.find((x) => x && x.content && x.content.offerType === 'VOLUME_DISCOUNT' && x.upsell && Array.isArray(x.upsell.volumeDiscountTiers));
      if (!f) return null;
      const tiers = f.upsell.volumeDiscountTiers.map((t) => ({
        q: Number(t.quantity),
        pct: t.discountType === 'PERCENTAGE' ? Number(t.discountValue) : t.discountType === 'NONE' ? 0 : NaN,
      })).sort((a, b) => a.q - b.q);
      if (!tiers.length || tiers[0].q !== 1 || tiers.some((t) => !(t.q >= 1) || !(t.pct >= 0 && t.pct < 100))) return null;
      return tiers;
    } catch (e) { return null; }
  }

  function readVariant() {
    const r = root(); if (!r) return null;
    const s = r.querySelector('script[data-selected-variant]');
    try { const v = JSON.parse(s.textContent); return v && Number.isInteger(v.price) ? v : null; } catch (e) { return null; }
  }

  const qtyInput = () => { const r = root(); return r && r.querySelector('quantity-set input[name="quantity"]'); };
  const quantity = () => { const i = qtyInput(); const q = i ? parseInt(i.value, 10) : 1; return q >= 1 ? q : 1; };
  const tierFor = (tiers, q) => tiers.reduce((best, t) => (q >= t.q ? t : best), tiers[0]);
  // The discount's arithmetic, as measured in the basket (6 Oct, test B): the discount is worked out per unit and rounded
  // DOWN to the penny, then multiplied (GBP29.99 at 3% = 89.97p, so 89p off each: 3 for GBP87.30, not 87.27).
  const unitAfter = (price, pct) => price - Math.floor(price * pct / 100 + 1e-9);
  const lineTotal = (price, q, pct) => unitAfter(price, pct) * q;

  /* ------------------------------------------------------------------ delivery card */
  let tick = null;
  function removeCard() {
    const r = root(); if (!r) return;
    r.querySelectorAll('.topiary-dcard').forEach((e) => e.remove());
    r.querySelectorAll('.topiary-app-replaced-eed, .topiary-soldout-eed').forEach((e) => e.classList.remove('topiary-app-replaced-eed', 'topiary-soldout-eed'));
    if (tick) { clearInterval(tick); tick = null; }
    document.dispatchEvent(new CustomEvent('topiary:delivery', { detail: null }));
  }

  // A sold-out variant gets no delivery promise (the app still counts down to a delivery date for it). Only when the
  // variant reads as unavailable: if it can't be read at all, the card behaves as before.
  const soldOut = () => { const v = readVariant(); return !!v && v.available === false; };

  function renderCard() {
    if (soldOut()) {
      removeCard();
      const b = root() && root().querySelector('[data-block-key*="essential_estimated"]');
      if (b) b.classList.add('topiary-soldout-eed');
      return;
    }
    const d = readDelivery();
    if (!d) { removeCard(); return; }
    let card = d.block.querySelector(':scope > .topiary-dcard');
    if (!card) {
      card = document.createElement('div');
      card.className = 'topiary-dcard';
      card.innerHTML =
        '<div class="topiary-dcard-when"><div class="topiary-cal" aria-hidden="true"><i></i><b></b><u></u></div>' +
        '<div><div class="topiary-cap">Arrives by</div><b class="topiary-date"></b><span class="topiary-next"></span></div></div>' +
        '<div class="topiary-dcard-clock"><div class="topiary-cap">Order within</div>' +
        '<div class="topiary-digits" role="timer" aria-live="off"><span><b class="h"></b><small>hrs</small></span><span><b class="m"></b><small>mins</small></span><span><b class="s"></b><small>secs</small></span></div>' +
        '<div class="topiary-free">Free delivery</div></div>';
      d.block.prepend(card);
    }
    // "Next working day" = delivered the working day after the order is processed, i.e. after the cut-off's own day.
    const nextWorking = new Date(d.cutoff.getFullYear(), d.cutoff.getMonth(), d.cutoff.getDate());
    do { nextWorking.setDate(nextWorking.getDate() + 1); } while (nextWorking.getDay() === 0 || nextWorking.getDay() === 6);
    card.querySelector('.topiary-cal i').textContent = MONTHS[d.date.getMonth()].toUpperCase();
    card.querySelector('.topiary-cal b').textContent = d.date.getDate();
    card.querySelector('.topiary-cal u').textContent = DAYS[d.date.getDay()].slice(0, 3);
    card.querySelector('.topiary-date').textContent = DAYS[d.date.getDay()] + ' ' + d.date.getDate() + ' ' + FULL_MONTHS[d.date.getMonth()];
    card.querySelector('.topiary-next').textContent = d.date.getTime() === nextWorking.getTime() ? 'Next working day' : '';
    d.block.classList.add('topiary-app-replaced-eed');
    document.dispatchEvent(new CustomEvent('topiary:delivery', { detail: { date: d.date } }));
    const draw = () => {
      const left = Math.max(0, Math.floor((d.cutoff.getTime() - Date.now()) / 1000));
      card.querySelector('.h').textContent = pad(Math.floor(left / 3600));
      card.querySelector('.m').textContent = pad(Math.floor((left % 3600) / 60));
      card.querySelector('.s').textContent = pad(left % 60);
      if (left === 0) { clearInterval(tick); tick = null; setTimeout(renderCard, 4000); } // the app moves its date on; read it again
    };
    if (tick) clearInterval(tick);
    draw(); tick = setInterval(draw, 1000);
  }

  /* ------------------------------------------------------------------ tiles and the button total */
  function removeTiles() {
    const r = root(); if (!r) return;
    r.querySelectorAll('.topiary-tiles').forEach((e) => e.remove());
    r.querySelectorAll('.topiary-app-replaced-upsell, .topiary-soldout-upsell').forEach((e) => e.classList.remove('topiary-app-replaced-upsell', 'topiary-soldout-upsell'));
  }

  function renderTiles() {
    if (!DESKTOP.matches) return;
    const r = root(); const tiers = SHOW_DISCOUNTED ? readTiers() : null; const v = readVariant();
    const block = r && r.querySelector('[data-block-key*="essential_upsell"]');
    if (block && v && v.available === false) { removeTiles(); block.classList.add('topiary-soldout-upsell'); return; } // no bulk prices for a sold-out variant
    if (!block || !tiers || !v) { removeTiles(); return; }
    block.classList.remove('topiary-soldout-upsell');
    let box = block.querySelector(':scope > .topiary-tiles');
    if (!box) {
      box = document.createElement('div');
      box.className = 'topiary-tiles';
      block.prepend(box);
      box.addEventListener('click', (e) => {
        const t = e.target.closest('[data-tier]'); if (!t) return;
        setQuantity(Number(t.getAttribute('data-tier')));
      });
    }
    // Build the buttons only when the variant, its price or the tiers change; otherwise update the selected state in
    // place, so a focused or half-pressed tile is never replaced under the customer (Codex round 1, finding 1).
    const sig = v.id + '|' + v.price + '|' + tiers.map((t) => t.q + ':' + t.pct).join(',');
    if (box.dataset.sig !== sig) {
      box.innerHTML = '<div class="topiary-tiles-h">Buy more, save more</div><div class="topiary-tiles-row">' + tiers.map((t) => {
        const eachIncl = unitAfter(v.price, t.pct);                          // pence, what the basket charges per unit
        const eachEx = Math.round(eachIncl * 100 / 120);
        return '<button type="button" class="topiary-tile" data-tier="' + t.q + '" aria-pressed="false">' +
          '<span class="topiary-tile-q">' + (t.q === 1 ? '1 item' : t.q + '+') + '</span>' +
          // Inc VAT first, as the headline price above (Georgie, 13 Oct); ex VAT beneath for business buyers.
          '<span class="topiary-tile-p">' + gbp(eachIncl) + ' <small>each</small></span>' +
          '<span class="topiary-tile-x">' + gbp(eachEx) + ' ex VAT</span>' +
          (t.pct > 0 ? '<span class="topiary-tile-s">SAVE ' + t.pct + '%</span>' : '<span class="topiary-tile-s is-none">Full price</span>') + '</button>';
      }).join('') + '</div>';
      box.dataset.sig = sig;
    }
    const active = tierFor(tiers, quantity());
    box.querySelectorAll('.topiary-tile').forEach((b) => {
      const on = Number(b.getAttribute('data-tier')) === active.q;
      if (b.classList.contains('is-on') !== on) b.classList.toggle('is-on', on);
      if (b.getAttribute('aria-pressed') !== String(on)) b.setAttribute('aria-pressed', String(on));
    });
    block.classList.add('topiary-app-replaced-upsell');
  }

  function setQuantity(n) {
    const input = qtyInput(); if (!input || !(n >= 1)) return;
    // Not a `change` event: the theme's <quantity-set> treats any change on the input as a minus press
    // (product-info.js:339-355). Set the value, let the element refresh its own buttons, tell our listeners.
    input.value = String(n);
    const set = input.closest('quantity-set');
    if (set && typeof set.updateButtonState === 'function') set.updateButtonState();
    document.dispatchEvent(new CustomEvent('topiary:quantity', { detail: { q: n } }));
  }

  const totalLabel = () => { const r = root(); const btn = r && r.querySelector('[id^="ProductSubmitButton"]'); return btn && { btn, label: btn.querySelector('.product-card-atc-text') }; };

  // Put the theme's own label back, but only over text we wrote (if the theme has since written e.g. "Sold out", leave it).
  function restoreTotal() {
    const x = totalLabel(); if (!x || !x.label) return;
    const L = x.label;
    if (L.dataset.topiaryWrote && L.textContent.trim() === L.dataset.topiaryWrote && L.textContent !== L.dataset.topiaryOrig) L.textContent = L.dataset.topiaryOrig;
    delete L.dataset.topiaryWrote;
  }

  function renderTotal() {
    if (!DESKTOP.matches) return;
    const x = totalLabel(); if (!x || !x.label) return;
    const { btn, label } = x;
    const ours = label.dataset.topiaryWrote && label.textContent.trim() === label.dataset.topiaryWrote;
    if (!ours) { label.dataset.topiaryOrig = label.textContent.trim(); delete label.dataset.topiaryWrote; }
    const tiers = SHOW_DISCOUNTED ? readTiers() : null; const v = readVariant(); const q = quantity();
    if (!tiers || !v || btn.disabled || !v.available || q < 2) { if (ours) restoreTotal(); return; }
    const t = tierFor(tiers, q);
    const text = 'Add ' + q + ' to cart · ' + gbp(lineTotal(v.price, q, t.pct)) + ' inc VAT';
    if (label.textContent !== text) label.textContent = text;          // write only on change: no mutation, no refresh loop
    label.dataset.topiaryWrote = text;
  }

  /* ------------------------------------------------------------------ sticky bar: the delivery date (3.18) */
  // Follows the delivery card: the same validated read, cleared when the card is removed (fail open = no date shown).
  // The theme swaps the bar's inner HTML on variant change, which empties our span: an observer puts it back.
  let stickyDate = null;
  function renderStickyDate() {
    const want = DESKTOP.matches && stickyDate
      ? 'Arrives ' + DAYS[stickyDate.getDay()] + ' ' + stickyDate.getDate() + ' ' + FULL_MONTHS[stickyDate.getMonth()] : '';
    document.querySelectorAll('[data-sticky-atc] .topiary-sticky-date').forEach((el) => { if (el.textContent !== want) el.textContent = want; });
  }

  /* ------------------------------------------------------------------ "Add to your order" heading (desktop wording) */
  function renderAddonsHeading() {
    const r = root(); const sum = r && r.querySelector('complementary-product summary.complementary-product-heading');
    if (!sum) return;
    const textNode = [...sum.childNodes].find((n) => n.nodeType === 3 && n.textContent.trim());
    if (!textNode) return;
    if (!sum.dataset.topiaryOrig) sum.dataset.topiaryOrig = textNode.textContent;
    const want = DESKTOP.matches ? ' Add to your order ' : sum.dataset.topiaryOrig;
    if (textNode.textContent !== want) textNode.textContent = want;
  }

  /* ------------------------------------------------------------------ About | Reviews: the review list, desktop only (3.15) */
  // The Reviews.io block is shipped inside a <template> (never run by the browser), so phones load none of it. Here, at
  // 1025+ and after the page has loaded (so Reviews.io's loader, deferred elsewhere on the page, has run), it is copied
  // into the page once; scripts are re-created so they execute. The widget's start-up gives up if the loader isn't ready,
  // so it is nudged through the hook it exposes. The column shows only once the widget has started: if it never does,
  // About keeps the full width (fail open).
  function mountReviews() {
    const tpl = document.querySelector('.topiary-about-reviews template.topiary-rv-tpl');
    if (!tpl || tpl.dataset.mounted) return;
    tpl.dataset.mounted = '1';
    const frag = document.importNode(tpl.content, true);
    frag.querySelectorAll('script').forEach((old) => {
      const s = document.createElement('script');
      [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
    tpl.after(frag);
    const duo = tpl.closest('.topiary-duo');
    let tries = 0;
    const poll = setInterval(() => {
      const w = duo && duo.querySelector('[data-reviewsio-widget="polaris"]');
      if (w && w.getAttribute('data-reviewsio-initialized') === 'true') { duo.classList.add('has-reviews'); clearInterval(poll); return; }
      if (!w || ++tries > 40) { clearInterval(poll); return; }
      if (typeof window.__reviewsioInitPolarisWidgets === 'function') { try { window.__reviewsioInitPolarisWidgets(); } catch (e) {} }
    }, 250);
  }

  /* ------------------------------------------------------------------ wiring */
  let started = false;
  function refresh() {
    if (!DESKTOP.matches) return;
    try { renderCard(); } catch (e) { try { removeCard(); } catch (x) {} }
    try { renderTiles(); } catch (e) { try { removeTiles(); } catch (x) {} }
    try { renderTotal(); } catch (e) {}
    try { renderAddonsHeading(); } catch (e) {}
  }
  const soon = (() => { let t = null; return () => { clearTimeout(t); t = setTimeout(refresh, 150); }; })();

  // Below 1025 px nothing of ours may remain: the apps' own blocks and the theme's button label come back (Codex round 1,
  // finding 2). Coming back above 1025 redraws from the current variant and quantity.
  function teardown() {
    try { removeCard(); } catch (e) {}     // also stops the countdown and clears the sticky date
    try { removeTiles(); } catch (e) {}
    try { restoreTotal(); } catch (e) {}
    try { renderAddonsHeading(); renderStickyDate(); } catch (e) {}
  }

  // A mutation is ours (and ignored) when it happens inside our card or tiles, or adds/removes only our own elements.
  // Classes persist on detached nodes, so removed nodes are recognised too. The countdown ticks inside the card.
  const OURS = '.topiary-dcard, .topiary-tiles';
  const ourNode = (n) => n.nodeType === 1 && (n.matches(OURS) || !!n.closest(OURS));
  const ourMutation = (m) => {
    const t = m.target.nodeType === 1 ? m.target : m.target.parentElement;
    if (t && t.closest && t.closest(OURS)) return true;
    const nodes = [...m.addedNodes, ...m.removedNodes];
    return nodes.length > 0 && nodes.every(ourNode);
  };

  function start() {
    if (started || !DESKTOP.matches || !root()) return;
    started = true;
    const mount = () => { try { mountReviews(); } catch (e) {} };
    if (document.readyState === 'complete') mount(); else window.addEventListener('load', mount, { once: true });
    document.addEventListener('topiary:delivery', (e) => { stickyDate = (e.detail && e.detail.date) || null; renderStickyDate(); });
    const sticky = document.querySelector('[data-sticky-atc]');
    if (sticky) new MutationObserver((list) => {
      if (list.some((m) => !(m.target.closest && m.target.closest('.topiary-sticky-date')))) renderStickyDate();
    }).observe(sticky, { childList: true, subtree: true });
    refresh();
    // The apps render after load and re-render on variant changes: watch the two blocks (and the buy box for the swaps).
    const r = root();
    // Also the delivery app's cut-off attribute: if it changes or goes bad after we've drawn, re-read (fail open). We never
    // write that attribute, so watching it can't feed back into our own updates.
    new MutationObserver((list) => {
      if (DESKTOP.matches && list.some((m) => !ourMutation(m))) soon();
    }).observe(r.querySelector('.product-blocks') || r, { childList: true, subtree: true, attributes: true, attributeFilter: ['cutoff-date'] });
    r.addEventListener('change', (e) => { if (e.target && e.target.name === 'quantity') setTimeout(() => { renderTiles(); renderTotal(); }, 150); });
    r.addEventListener('click', (e) => { if (e.target.closest('quantity-set .quantity-button')) setTimeout(() => { renderTiles(); renderTotal(); }, 150); });
    document.addEventListener('topiary:quantity', () => { renderTiles(); renderTotal(); });
    document.addEventListener('variant:change', soon);
  }

  DESKTOP.addEventListener('change', () => {
    if (!DESKTOP.matches) { teardown(); return; }
    if (!started) start(); else refresh();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  window.topiaryPdp = { stage: 3, refresh, readDelivery, readTiers };
})();
